import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useWebSocket } from "../../contexts/WebSocketContext.jsx";
import { apiJson } from "../../utils/api.js";
import { fmtDt, userTitle } from "../../utils/users.js";
import { useDebouncedValue } from "../../hooks/useDebouncedValue.js";
import { useMe } from "../../hooks/useMe.js";
import { CrmRow } from "./CrmRow.jsx";
import { MessengerBadge } from "./MessengerBadge.jsx";
import { InlineBanner } from "./InlineBanner.jsx";
import { ListEmpty } from "./ListEmpty.jsx";
import { PageReveal } from "./PageReveal.jsx";
import { FiltersModal } from "./FiltersModal.jsx";
import { FilterChips } from "./FilterChips.jsx";

const PAGE_SIZE = 30;
const FILTERS_SESSION_KEY = "crm:dialogs:filters:v1";

function initialsFromName(name) {
  const s = String(name || "").trim();
  if (!s) return "—";
  const parts = s.split(/\s+/).filter(Boolean);
  const a = (parts[0] || "").slice(0, 1);
  const b = (parts[1] || "").slice(0, 1);
  return (a + b).toUpperCase();
}

function dialogLastMessagePrefix(senderName) {
  const name = String(senderName || "").trim();
  return name ? `${name}: ` : "";
}

function readFiltersSnapshot() {
  try {
    const raw = sessionStorage.getItem(FILTERS_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function writeFiltersSnapshot(snap) {
  try {
    sessionStorage.setItem(FILTERS_SESSION_KEY, JSON.stringify(snap || {}));
  } catch {
    // ignore
  }
}

const FilterIcon = (
  <svg className="crmIconSvg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 6h16" />
    <path d="M7 12h10" />
    <path d="M10 18h4" />
  </svg>
);

export function DialogsList({ onOpenChat }) {
  const { isAdmin, isRop, isModerator } = useMe();
  const canFilterByManager = isAdmin || isRop || isModerator;

  const snap0 = useMemo(() => readFiltersSnapshot(), []);
  const [q, setQ] = useState("");
  const qDebounced = useDebouncedValue(q, 300);
  const [managerFilter, setManagerFilter] = useState(() =>
    typeof snap0?.managerFilter === "string" ? snap0.managerFilter : ""
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { subscribe } = useWebSocket();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [rowCtx, setRowCtx] = useState(null);
  const [rowCtxPos, setRowCtxPos] = useState(null);
  const rowCtxRef = useRef(null);
  const [markUnreadErr, setMarkUnreadErr] = useState(null);

  const scrollRef = useRef(null);
  const query = useMemo(() => qDebounced.trim(), [qDebounced]);
  const loadPageRef = useRef(null);

  const closeRowCtx = useCallback(() => {
    setRowCtx(null);
    setRowCtxPos(null);
  }, []);

  useEffect(() => {
    writeFiltersSnapshot({ managerFilter });
  }, [managerFilter]);

  // Если роль не позволяет — на всякий случай скидываем сохранённый фильтр.
  useEffect(() => {
    if (!canFilterByManager && managerFilter) {
      setManagerFilter("");
    }
  }, [canFilterByManager, managerFilter]);

  async function loadPage({ reset }) {
    if (loading || loadingMore) return;
    if (reset) setLoading(true);
    else setLoadingMore(true);

    setError(null);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(reset ? 0 : offset),
        dialogs_only: "1",
        include_filters: "1",
      });
      if (query) params.set("q", query);
      if (canFilterByManager && managerFilter) {
        params.set("manager_id", managerFilter);
      }
      const data = await apiJson(`/api/users?${params.toString()}`);
      const newItems = data.items || [];
      const newTotal = data.total || 0;
      setTotal(newTotal);
      if (data.managers && Array.isArray(data.managers.items)) {
        setManagers(data.managers.items);
      }
      if (reset) {
        setItems(newItems);
        setOffset(newItems.length);
      } else {
        setItems((prev) => [...prev, ...newItems]);
        setOffset((prev) => prev + newItems.length);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      if (reset) {
        setItems([]);
        setTotal(0);
        setOffset(0);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }

  loadPageRef.current = loadPage;

  useEffect(() => {
    loadPage({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, managerFilter, canFilterByManager]);

  useEffect(() => {
    const unsub = subscribe((payload) => {
      if (payload.type === "new_message" && loadPageRef.current) {
        loadPageRef.current({ reset: true });
      }
    });
    return unsub;
  }, [subscribe]);

  useEffect(() => {
    if (!rowCtx) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeRowCtx();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [rowCtx, closeRowCtx]);

  useEffect(() => {
    if (!rowCtx) return;
    const M = 8;
    const raf = requestAnimationFrame(() => {
      const el = rowCtxRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vw = window.innerWidth || 0;
      const vh = window.innerHeight || 0;
      let x = Number(rowCtx.x) || 0;
      let y = Number(rowCtx.y) || 0;
      if (vw > 0) x = Math.max(M, Math.min(x, vw - rect.width - M));
      else x = Math.max(M, x);
      if (vh > 0) y = Math.max(M, Math.min(y, vh - rect.height - M));
      else y = Math.max(M, y);
      setRowCtxPos({ x, y });
    });
    return () => cancelAnimationFrame(raf);
  }, [rowCtx]);

  const markRowUnread = useCallback(async () => {
    if (!rowCtx) return;
    const crmId = rowCtx.crmUserId;
    closeRowCtx();
    setMarkUnreadErr(null);
    try {
      const data = await apiJson(
        `/api/messages/mark-unread?user_id=${encodeURIComponent(String(crmId))}`,
        { method: "POST" }
      );
      const hasUnread = Boolean(data?.has_unread);
      setItems((prev) => prev.map((it) => (it.id === crmId ? { ...it, has_unread: hasUnread } : it)));
    } catch (e) {
      setMarkUnreadErr(e instanceof Error ? e.message : String(e));
    }
  }, [rowCtx, closeRowCtx]);

  const hasMore = offset < total;

  function isNearBottom(el, threshold = 150) {
    if (!el) return false;
    return el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
  }

  const showFilterButton = canFilterByManager && managers.length > 1;
  const hasActiveFilters = !!managerFilter;
  const managerTitle = managerFilter
    ? managers.find((m) => String(m.id) === String(managerFilter))?.display_name || managerFilter
    : "";
  const filterChips = [
    managerFilter && {
      key: "manager",
      label: `Менеджер: ${managerTitle}`,
      onRemove: () => setManagerFilter(""),
    },
  ].filter(Boolean);
  const onResetFilters = () => setManagerFilter("");

  return (
    <main
      className="chat"
      id="dialogsScreen"
    >
      <div className="crmStickyStack">
        {markUnreadErr ? (
          <InlineBanner
            message={markUnreadErr}
            variant="error"
            onClose={() => setMarkUnreadErr(null)}
          />
        ) : null}
        <div className="crmToolbar">
          <div className="crmToolbarRow">
            <input
              className="input crmSearchInput"
              id="q"
              placeholder="Поиск: имя, телефон или id"
              autoComplete="off"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            {showFilterButton ? (
              <button
                type="button"
                className={`crmIconBtn ${hasActiveFilters ? "isActive" : ""}`}
                title="Фильтры"
                aria-label="Фильтры"
                onClick={() => setFiltersOpen(true)}
              >
                {FilterIcon}
              </button>
            ) : null}
          </div>
          {showFilterButton && hasActiveFilters ? (
            <FilterChips chips={filterChips} onReset={onResetFilters} />
          ) : null}
        </div>
      </div>

      <div className="section">
        <div
          className="crmListSurface"
          id="list"
          ref={scrollRef}
          onScroll={() => {
            const el = scrollRef.current;
            if (!el) return;
            if (hasMore && !loading && !loadingMore && isNearBottom(el)) {
              loadPage({ reset: false });
            }
          }}
        >
          <PageReveal ready={!loading}>
            {error ? (
              <ListEmpty message="Ошибка загрузки" />
            ) : !items.length ? (
              <ListEmpty message="Нет диалогов по фильтрам" />
            ) : (
              <>
                {items.map((u) => (
                <CrmRow
                  key={String(u.id)}
                  barClass={u.in_refusals_base ? "warn" : "good"}
                  avatar={
                    <span className={`avatar ${u.has_unread ? "isUnread" : ""}`} aria-hidden="true">
                      {initialsFromName(userTitle(u))}
                    </span>
                  }
                  title={
                    <span className="crmRowTitleCluster">
                      <span className="crmRowTitleName">{userTitle(u)}</span>
                      <MessengerBadge messenger={u.messenger} />
                      {u.in_refusals_base ? (
                        <span className="refusalBadge" title="Клиент в базе отказов">
                          Отказ
                        </span>
                      ) : null}
                      {u.has_unread ? <span className="rowUnreadDot" title="Есть непрочитанные" /> : null}
                    </span>
                  }
                  right={fmtDt(u.last_message_at || u.first_contact_at)}
                  bottom={
                    <span className="crmMeta crmLastMessagePreview" title={u.last_message_preview || undefined}>
                      {u.last_message_preview
                        ? `${dialogLastMessagePrefix(u.last_message_sender_name)}${u.last_message_preview}`
                        : "Нет сообщений"}
                    </span>
                  }
                  onClick={() => onOpenChat?.(u.id)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setMarkUnreadErr(null);
                    setRowCtx({ x: e.clientX, y: e.clientY, crmUserId: u.id });
                    setRowCtxPos({ x: e.clientX, y: e.clientY });
                  }}
                />
              ))}
              </>
            )}
          </PageReveal>
        </div>
      </div>

      {rowCtx ? (
        <div
          className="msgCtxBackdrop"
          onClick={closeRowCtx}
          onContextMenu={(e) => {
            e.preventDefault();
            closeRowCtx();
          }}
        >
          <div
            className="crmListCtxMenu"
            ref={rowCtxRef}
            style={{ left: rowCtxPos?.x ?? rowCtx.x, top: rowCtxPos?.y ?? rowCtx.y }}
            role="menu"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="msgCtxItem crmListCtxItem"
              role="menuitem"
              onClick={() => {
                void markRowUnread();
              }}
            >
              Пометить как непрочитанное
            </button>
          </div>
        </div>
      ) : null}

      {showFilterButton ? (
        <FiltersModal open={filtersOpen} onClose={() => setFiltersOpen(false)}>
          <div className="dealsFiltersBody">
            <div className="dealsFilterRow">
              <label className="dealsFilterLabel">Менеджер</label>
              <select
                className="dealsFilterSelect"
                value={managerFilter}
                onChange={(e) => setManagerFilter(e.target.value)}
              >
                <option value="">Все</option>
                {managers.map((m) => (
                  <option key={m.id} value={m.id}>{m.display_name}</option>
                ))}
              </select>
            </div>
            <div className="dealsFilterActions">
              <button
                type="button"
                className="linkBtn"
                style={{ flex: 1, justifyContent: "center" }}
                onClick={() => setFiltersOpen(false)}
              >
                Применить
              </button>
              <button
                type="button"
                className="crmIconBtn"
                title="Сбросить"
                onClick={onResetFilters}
              >
                ↺
              </button>
            </div>
          </div>
        </FiltersModal>
      ) : null}
    </main>
  );
}
